import { useEffect, useState } from "react";
import { Users, Shield, FileText, Inbox, ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatDateTime, formatDate } from "@/lib/format";

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="gov-card p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-muted-foreground mb-1">{label}</div>
          <div className="font-display text-3xl font-bold font-mono-num">{value}</div>
        </div>
        <div
          className="w-10 h-10 rounded-sm flex items-center justify-center"
          style={{ background: `hsl(var(--${accent}) / 0.1)`, color: `hsl(var(--${accent}))` }}
        >
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

const STATUS_BADGE = {
  draft: { label: "مسودة", cls: "bg-muted text-muted-foreground" },
  submitted: { label: "مقدمة", cls: "bg-info/10 text-info" },
  approved: { label: "معتمدة", cls: "bg-success/10 text-success" },
  rejected: { label: "مرفوضة", cls: "bg-destructive/10 text-destructive" },
};

export default function DashboardHome() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="dashboard-home">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight">لوحة التحكم</h1>
          <p className="text-muted-foreground mt-1">نظرة عامة على نظام إدارة الجوازات</p>
        </div>
        <div className="text-sm text-muted-foreground font-mono">
          {formatDate(new Date())}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Inbox} label="الاستمارات المقدمة" value={stats?.submissions_count ?? "—"} accent="primary" testid="stat-submissions" />
        <StatCard icon={FileText} label="النماذج النشطة" value={stats?.templates_count ?? "—"} accent="secondary" testid="stat-templates" />
        <StatCard icon={Users} label="الموظفون" value={stats?.users_count ?? "—"} accent="info" testid="stat-users" />
        <StatCard icon={Shield} label="الأدوار" value={stats?.roles_count ?? "—"} accent="success" testid="stat-roles" />
      </div>

      <div className="gov-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border bg-surface">
          <h2 className="font-display text-lg font-semibold">آخر الاستمارات</h2>
          <Link to="/submissions" className="text-xs text-primary hover:underline flex items-center gap-1">
            عرض الكل <ChevronLeft className="w-3.5 h-3.5" />
          </Link>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-surface/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-start px-5 py-3 font-medium">رقم الاستمارة</th>
              <th className="text-start px-5 py-3 font-medium">النموذج</th>
              <th className="text-start px-5 py-3 font-medium">المُقدِّم</th>
              <th className="text-start px-5 py-3 font-medium">الحالة</th>
              <th className="text-start px-5 py-3 font-medium">التاريخ</th>
            </tr>
          </thead>
          <tbody>
            {(stats?.recent_submissions || []).map((s) => {
              const st = STATUS_BADGE[s.status] || STATUS_BADGE.submitted;
              return (
                <tr key={s.id} className="border-b border-border/60 last:border-0 hover:bg-surface/40">
                  <td className="px-5 py-3 font-mono-num font-medium">{s.form_number}</td>
                  <td className="px-5 py-3">{s.template_title}</td>
                  <td className="px-5 py-3 text-muted-foreground">{s.submitted_by_name}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>
                      {st.label}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-muted-foreground font-mono text-xs">
                    {formatDateTime(s.submitted_at)}
                  </td>
                </tr>
              );
            })}
            {!stats?.recent_submissions?.length && (
              <tr>
                <td colSpan={5} className="px-5 py-12 text-center text-muted-foreground">
                  لا توجد استمارات بعد
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
